import React from 'react';
import { 
  LayoutDashboard, Link2, FileText, Shield, AlertTriangle, 
  CheckCircle2, TrendingUp, Award, Flame, Target, Crown, Star, Eye, Zap, Trophy, Mail,
  GitCompare, SearchX, FlaskConical, Microscope
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useApp } from '@/context/AppContext';
import { BADGES } from '@/lib/verificationLogic';
import { cn } from '@/lib/utils';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const iconMap = {
  'award': Award,
  'shield': Shield,
  'eye': Eye,
  'star': Star,
  'crown': Crown,
  'flame': Flame,
  'zap': Zap,
  'trophy': Trophy,
  'alert-triangle': AlertTriangle,
  'mail': Mail,
  'git-compare': GitCompare,
  'search-x': SearchX,
  'flask-conical': FlaskConical,
  'microscope': Microscope,
};

const StatCard = ({ title, value, icon: Icon, trend, color, description }) => (
  <Card className="hover:shadow-lg transition-all duration-200">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className={cn("text-3xl font-bold font-display", color)}>{value}</p>
          {description && (
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", 
          color === "text-success" ? "bg-success/10" :
          color === "text-warning" ? "bg-warning/10" :
          color === "text-destructive" ? "bg-destructive/10" :
          "bg-primary/10"
        )}>
          <Icon className={cn("w-6 h-6", color || "text-primary")} />
        </div>
      </div>
      {trend && (
        <div className="flex items-center gap-1 mt-3">
          <TrendingUp className="w-4 h-4 text-success" />
          <span className="text-xs text-success">{trend}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const BadgeCard = ({ badge, earned }) => {
  const Icon = iconMap[badge.icon] || Award;
  
  return (
    <div className={cn(
      "flex items-center gap-4 p-4 rounded-xl border transition-all duration-200",
      earned 
        ? "bg-primary/5 border-primary/30 shadow-sm" 
        : "bg-muted/30 border-border opacity-50"
    )}>
      <div className={cn(
        "w-12 h-12 rounded-xl flex items-center justify-center",
        earned ? "gradient-primary" : "bg-muted"
      )}>
        <Icon className={cn("w-6 h-6", earned ? "text-primary-foreground" : "text-muted-foreground")} />
      </div>
      <div className="flex-1">
        <p className={cn("font-medium", earned ? "text-foreground" : "text-muted-foreground")}>
          {badge.name}
        </p>
        <p className="text-xs text-muted-foreground">{badge.description}</p>
      </div>
      {earned && (
        <Badge variant="safe">Earned</Badge>
      )}
    </div>
  );
};

export const Dashboard = () => {
  const { stats, earnedBadges, nextBadge, history } = useApp();

  // Calculate percentages for pie chart
  const totalResults = stats.safeFound + stats.suspiciousFound + stats.dangerousFound;
  const pieData = totalResults > 0 ? [
    { name: 'Safe', value: stats.safeFound, color: 'hsl(var(--success))' },
    { name: 'Suspicious', value: stats.suspiciousFound, color: 'hsl(var(--warning))' },
    { name: 'Dangerous', value: stats.dangerousFound, color: 'hsl(var(--destructive))' },
  ].filter(d => d.value > 0) : [];

  // Recent activity data for bar chart
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    date.setHours(0, 0, 0, 0);
    return date.toISOString().split('T')[0];
  });

  const activityData = last7Days.map(day => {
    const count = history.filter(h => {
      const hDate = new Date(h.timestamp);
      return hDate.toISOString().split('T')[0] === day;
    }).length;
    return {
      day: new Date(day).toLocaleDateString('en', { weekday: 'short' }),
      count,
    };
  });

  // Calculate progress to next badge
  const getNextBadgeProgress = () => {
    if (!nextBadge) return 100;
    
    if (nextBadge.type === 'streak') {
      return Math.min((stats.streak / nextBadge.threshold) * 100, 100);
    } else if (nextBadge.type === 'dangerous') {
      return Math.min((stats.dangerousFound / nextBadge.threshold) * 100, 100);
    }
    return Math.min((stats.totalVerifications / nextBadge.threshold) * 100, 100);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
            <LayoutDashboard className="w-5 h-5 text-primary-foreground" />
          </div>
          <h2 className="font-display text-2xl font-bold text-foreground">Dashboard</h2>
        </div>
        <p className="text-muted-foreground">
          Track your verification activity, streaks, and achievements.
        </p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Verified"
          value={stats.totalVerifications}
          icon={Shield}
          color="text-primary"
          description="Links, articles & emails"
        />
        <StatCard
          title="Current Streak"
          value={`${stats.streak} days`}
          icon={Flame}
          color="text-warning"
          description="Keep it going!"
        />
        <StatCard
          title="Safe Found"
          value={stats.safeFound}
          icon={CheckCircle2}
          color="text-success"
          description="Clean verifications"
        />
        <StatCard
          title="Threats Detected"
          value={stats.dangerousFound + stats.suspiciousFound}
          icon={AlertTriangle}
          color="text-destructive"
          description="Potential risks caught"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Verification Results Pie Chart */}
        <GlassCard className="p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Verification Results</h3>
          {totalResults > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={160} height={160}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-success" />
                  <span className="text-sm text-muted-foreground">Safe: {stats.safeFound}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-warning" />
                  <span className="text-sm text-muted-foreground">Suspicious: {stats.suspiciousFound}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-destructive" />
                  <span className="text-sm text-muted-foreground">Dangerous: {stats.dangerousFound}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-muted-foreground">
              No verifications yet. Start checking links!
            </div>
          )}
        </GlassCard>

        {/* Activity Chart */}
        <GlassCard className="p-6">
          <h3 className="font-display font-semibold text-lg mb-4">7-Day Activity</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={activityData}>
              <XAxis 
                dataKey="day" 
                axisLine={false} 
                tickLine={false}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
              />
              <Bar 
                dataKey="count" 
                fill="hsl(var(--primary))" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>
      </div>

      {/* Type Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <Link2 className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Links Verified</p>
                <p className="text-2xl font-bold font-display">{stats.linksVerified}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">
                  {stats.totalVerifications > 0 
                    ? `${Math.round((stats.linksVerified / stats.totalVerifications) * 100)}%`
                    : '0%'}
                </p>
                <Progress 
                  value={stats.totalVerifications > 0 ? (stats.linksVerified / stats.totalVerifications) * 100 : 0} 
                  className="w-24 h-2 mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center">
                <FileText className="w-7 h-7 text-secondary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Articles Analyzed</p>
                <p className="text-2xl font-bold font-display">{stats.articlesVerified}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">
                  {stats.totalVerifications > 0 
                    ? `${Math.round((stats.articlesVerified / stats.totalVerifications) * 100)}%`
                    : '0%'}
                </p>
                <Progress 
                  value={stats.totalVerifications > 0 ? (stats.articlesVerified / stats.totalVerifications) * 100 : 0} 
                  className="w-24 h-2 mt-1"
                  variant="success"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center">
                <Mail className="w-7 h-7 text-accent" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Emails Checked</p>
                <p className="text-2xl font-bold font-display">{stats.emailsVerified || 0}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">
                  {stats.totalVerifications > 0 
                    ? `${Math.round(((stats.emailsVerified || 0) / stats.totalVerifications) * 100)}%`
                    : '0%'}
                </p>
                <Progress 
                  value={stats.totalVerifications > 0 ? ((stats.emailsVerified || 0) / stats.totalVerifications) * 100 : 0} 
                  className="w-24 h-2 mt-1"
                  variant="warning"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Next Badge Progress */}
      {nextBadge && (
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Next Achievement
            </h3>
            <Badge variant="glass">{Math.round(getNextBadgeProgress())}% complete</Badge>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center">
              {React.createElement(iconMap[nextBadge.icon] || Award, {
                className: "w-8 h-8 text-muted-foreground"
              })}
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground">{nextBadge.name}</p>
              <p className="text-sm text-muted-foreground">{nextBadge.description}</p>
              <Progress value={getNextBadgeProgress()} className="mt-2" variant="gradient" />
            </div>
          </div>
        </GlassCard>
      )}

      {/* Badges Section */}
      <div>
        <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-primary" />
          Achievements ({earnedBadges.length}/{BADGES.length})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {BADGES.map((badge) => (
            <BadgeCard 
              key={badge.id} 
              badge={badge} 
              earned={earnedBadges.some(b => b.id === badge.id)} 
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
